/**
 * Règles de passage d'une étape à l'autre du wizard — SOURCE UNIQUE.
 *
 * Lot Z1 : la condition du propriétaire était écrite DEUX fois (Step4_Owner pour activer le
 * bouton « Suivant », PropertyCreationWizard.canProceedToStep(5) pour autoriser le passage).
 * L'une gérait les personnes morales, l'autre non : créer un bien avec un propriétaire société
 * était impossible. Une seule règle vit désormais ici et les deux appelants l'appellent.
 *
 * Lot Z2 : les règles renvoient la LISTE DES CHAMPS MANQUANTS (libellés affichables), pas un
 * booléen. C'est cette liste qui est montrée à l'utilisateur — « Renseignez : Raison sociale,
 * Téléphone » — au lieu d'un message générique.
 */
import { getPriceLabel } from '../../constants/operationLabels';

/**
 * Personne morale ? Défaut = personne physique.
 * Tolère les valeurs héritées : `particulier` (état initial du wizard) et `societe`
 * (extraction IA d'un mandat) — sans quoi un particulier se voyait proposer le formulaire société.
 */
export const estPersonneMorale = (contact) =>
  ['personne_morale', 'societe', 'morale'].includes(String(contact?.type || '').toLowerCase());

/**
 * Champs manquants du propriétaire (étape 4).
 * Règle : un contact existant sélectionné OU — personne physique : nom + prénom,
 * personne morale : raison sociale — et dans les deux cas au moins un téléphone ou un courriel.
 *
 * @param {object} data  données du wizard
 * @param {'select'|'create'|undefined} mode  onglet actif de l'étape 4 ; sans mode, un contact
 *   existant sélectionné suffit (c'est le cas du wizard, qui ne connaît pas l'onglet).
 */
export const champsManquantsProprietaire = (data, mode) => {
  const nc = data?.nouveau_contact || {};
  if (mode === 'select') {
    return data?.contact_proprietaire_id ? [] : ['Contact propriétaire (sélectionnez-en un)'];
  }
  if (mode !== 'create' && data?.contact_proprietaire_id) return [];

  const manquants = [];
  if (estPersonneMorale(nc)) {
    if (!nc.societe) manquants.push('Raison sociale');
  } else {
    if (!nc.nom) manquants.push('Nom');
    if (!nc.prenom) manquants.push('Prénom');
  }
  if (!nc.telephone && !nc.email) manquants.push('Téléphone ou email');
  return manquants;
};

export const proprietaireComplet = (data, mode) =>
  champsManquantsProprietaire(data, mode).length === 0;

// Licence d'exploitation / parts sociales : actifs incorporels, pas de surface exigée.
const sansSurface = (data) =>
  data.operation === 'parts_sociales'
  || data.objet === 'licence' || data.objet === 'licence_exploitation'
  || (data.profil || '').toLowerCase().includes('licence')
  || (data.sous_profil || '').toLowerCase().includes('licence');

const champsManquantsTaxonomie = (data) => {
  const manquants = [];
  if (!data.operation) manquants.push('Type de transaction');
  if (!data.objet) manquants.push('Type de bien');
  if (!data.profil) manquants.push('Catégorie');
  if (!data.sous_profil) manquants.push('Activité précise');
  return manquants;
};

/**
 * Lot BI §7 — ce bien exige-t-il un loyer mensuel pour devenir actif ?
 * Règle UNIQUE : l'étape 3 (`isComplete`), le contrôle de passage d'étape et le serveur
 * (`_bi_exige_loyer`) décrivent la même chose. ⛔ Pas `location_gerance` (redevance, pas loyer).
 */
export const exigeLoyerMensuel = (data) => {
  const d = data || {};
  return d.operation === 'location'
    || d.operation === 'location_droit_entree'
    || (d.operation === 'cession' && d.objet === 'droit_au_bail');
};

/** Libellé du prix attendu en plus du loyer, quand le type de bien en demande un. */
export const LIBELLE_LOYER = 'Loyer HT (€/mois)';

const champsManquantsInfosCles = (data) => {
  const manquants = [];
  if (!data.adresse) manquants.push('Adresse');
  if (!data.ville) manquants.push('Ville');
  if (!data.code_postal) manquants.push('Code postal');
  if (data.operation === 'location_droit_entree') {
    if (!data.droit_entree) manquants.push('Pas-de-porte (€)');  // libellé identique au champ
    if (!data.loyer_mensuel) manquants.push(LIBELLE_LOYER);
  } else if (data.operation === 'cession' && data.objet === 'droit_au_bail') {
    // lot BI §7a — deux champs, comme la location avec pas-de-porte : le prix du droit au bail
    // ET le loyer, sans lequel SeLoger refuse l'annonce (distribution RENT sans `baseRent`).
    if (!data.prix) manquants.push('Prix du droit au bail (€)');
    if (!data.loyer_mensuel && !data.loyer) manquants.push(LIBELLE_LOYER);
  } else if (data.operation === 'location' && !data.loyer && !data.loyer_mensuel) {
    manquants.push(LIBELLE_LOYER);
  } else if (!data.prix && !data.loyer) {
    manquants.push(
      data.operation === 'cession' || data.operation === 'vente_immo'
        ? 'Prix net vendeur (€)'
        : getPriceLabel(data.operation),
    );
  }
  if (!sansSurface(data) && !data.surface_totale_m2) manquants.push('Surface (m²)');
  return manquants;
};

/**
 * Champs manquants pour ATTEINDRE l'étape `etapeCible` (donc ceux de l'étape précédente).
 * Liste vide = passage autorisé.
 */
export const champsManquants = (etapeCible, data) => {
  const d = data || {};
  switch (etapeCible) {
    case 2:
      return champsManquantsTaxonomie(d);
    case 3:
      return []; // étape 2 (type de dossier) entièrement optionnelle
    case 4:
      return champsManquantsInfosCles(d);
    case 5:
      return champsManquantsProprietaire(d);
    case 6:
      return d.negociateur ? [] : ['Négociateur'];
    default:
      return [];
  }
};

export const peutAvancerVers = (etapeCible, data) => champsManquants(etapeCible, data).length === 0;
